import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../components/feature/Footer';
import ScrollToTop from '../../components/feature/ScrollToTop';
import LoadingSpinner from '../../components/feature/LoadingSpinner';
import BackToHome from '../../components/feature/BackToHome';
import { fetchBlogPosts, type BlogPost } from '../../services/blogPosts';
import { fetchBlogCategories, type BlogCategory } from '../../services/blogCategories';
import { fetchBlogSection, type BlogSection } from '../../services/blogSection';
import Seo, { buildBreadcrumbJsonLd, SITE_URL } from '../../components/feature/Seo';

export default function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState('Alle');
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [blogSection, setBlogSection] = useState<BlogSection | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [postsData, categoriesData, blogSectionData] = await Promise.all([
          fetchBlogPosts(),
          fetchBlogCategories(),
          fetchBlogSection()
        ]);
        setBlogPosts(postsData);
        setCategories(categoriesData);
        setBlogSection(blogSectionData);
      } catch (error) {
        console.error('Failed to load blog data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const filteredPosts = selectedCategory === 'Alle'
    ? blogPosts
    : blogPosts.filter(post => post.category === selectedCategory);

  // Build category options including "Alle"
  const categoryOptions = ['Alle', ...categories.map(c => c.name)];

  const seoTitle = blogSection?.pageTitle
    ? `${blogSection.pageTitle} | Gerald Fasching`
    : 'Blog - Insights & Perspektiven | Gerald Fasching';
  const seoDescription =
    blogSection?.pageSubheading ||
    blogSection?.description ||
    'Insights, Perspektiven und Reflexionen von Gerald Fasching zu Führung, Bewusstseinsarbeit, Business- und Persönlichkeitsentwicklung sowie dem BHS-Modell - Business, Human, Spirit.';
  const blogListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    '@id': `${SITE_URL}/blog#blog`,
    url: `${SITE_URL}/blog`,
    name: seoTitle,
    description: seoDescription,
    inLanguage: 'de-AT',
    publisher: { '@id': `${SITE_URL}/#organization` },
    blogPost: blogPosts.slice(0, 12).map((p) => ({
      '@type': 'BlogPosting',
      headline: p.title,
      url: `${SITE_URL}/blog/${p.slug || p.id}`,
      datePublished: p.date || undefined,
      author: { '@type': 'Person', name: p.author || 'Gerald Fasching' },
      image: p.imageUrl || undefined,
    })),
  };
  const seo = (
    <Seo
      title={seoTitle}
      description={seoDescription}
      path="/blog"
      jsonLd={[
        blogListJsonLd,
        buildBreadcrumbJsonLd([
          { name: 'Start', path: '/' },
          { name: 'Blog', path: '/blog' },
        ]),
      ]}
    />
  );

  if (loading) {
    return (
      <>
        {seo}
        <LoadingSpinner bgColor="bg-[#FAF8F5]" />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {seo}
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#FAF8F5] via-[#F5F1EB] to-[#FAF8F5] px-6 pb-16 pt-32 lg:px-12">
        <div className="mx-auto max-w-7xl">
          <BackToHome className="mb-10 text-left" />
        </div>
        <div className="mx-auto max-w-7xl space-y-6 text-center">
          <div className="inline-flex items-center rounded-full bg-[#8B1538]/10 px-6 py-2">
            <span className="whitespace-nowrap text-sm font-medium text-[#8B1538] lg:text-base">{blogSection?.badgeText || 'Insights & Perspektiven'}</span>
          </div>
          <h1 className="text-[1.75rem] font-serif font-bold leading-snug text-[#1A2B3C] md:text-[2.125rem] lg:text-[2.75rem]" style={{ fontFamily: 'Lora, serif' }}>
            {blogSection?.pageTitle || 'Blog'}
          </h1>
          {(blogSection?.pageSubheading || blogSection?.description) && (
            <p className="mx-auto max-w-3xl text-base text-gray-700 lg:text-xl">
              {blogSection.pageSubheading || blogSection.description}
            </p>
          )}
        </div>
      </section>

      {/* Category Filter */}
      <section className="border-b border-gray-200 bg-white px-6 py-8 lg:px-12">
        <div className="mx-auto flex max-w-7xl flex-wrap justify-center gap-3">
          {categoryOptions.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-6 py-2 text-sm font-medium transition-all ${
                selectedCategory === category
                  ? 'bg-[#8B1538] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="px-6 py-16 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-600">{blogSection?.noArticlesText || 'Keine Artikel in dieser Kategorie gefunden.'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {filteredPosts.map((post) => (
                <article
                  key={post.id}
                  className="group flex flex-col overflow-hidden rounded-3xl bg-white shadow-lg transition-all duration-300 hover:shadow-xl"
                >
                  <Link to={`/blog/${post.slug || post.id}`} className="block cursor-pointer">
                    <div className="relative overflow-hidden" style={{ height: '240px' }}>
                      {post.imageUrl ? (
                        <img
                          src={post.imageUrl}
                          alt={post.title}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <i className="ri-image-line text-4xl text-gray-400"></i>
                        </div>
                      )}
                      {post.category && (
                        <div className="absolute top-4 left-4">
                          <span className="px-4 py-1.5 bg-[#8B1538] text-white text-xs lg:text-[13px] font-medium rounded-full whitespace-nowrap">
                            {post.category}
                          </span>
                        </div>
                      )}
                    </div>
                  </Link>
                  <div className="p-8 flex flex-col flex-grow">
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                      {post.date && (
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                          {new Date(post.date).toLocaleDateString('de-DE', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      )}
                      {post.readTime && (
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                          {post.readTime}
                        </span>
                      )}
                    </div>
                    <Link to={`/blog/${post.slug || post.id}`} className="block cursor-pointer mb-4">
                      <h2 className="text-xl font-serif font-bold text-[#1A2B3C] hover:text-[#8B1538] transition-colors" style={{ fontFamily: 'Lora, serif' }}>
                        {post.title}
                      </h2>
                    </Link>
                    {post.excerpt && (
                      <p className="text-gray-700 text-sm lg:text-base leading-relaxed line-clamp-3 flex-grow">
                        {post.excerpt}
                      </p>
                    )}
                    <div className="pt-4 mt-auto">
                      <Link
                        to={`/blog/${post.slug || post.id}`}
                        className="inline-flex items-center text-[#8B1538] text-sm lg:text-base font-medium hover:text-[#6B0F2A] transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {blogSection?.readMoreText || 'Weiterlesen'}
                        <i className="ri-arrow-right-line ml-2 w-4 h-4 flex items-center justify-center"></i>
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
      <ScrollToTop />
    </div>
  );
}
